import React from 'react';
import { Box, Button, Collapse, Grow, IconButton, LinearProgress, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate } from 'react-router-dom';
import EtapeLigne from './EtapeLigne';
import { useOnboarding } from './OnboardingProvider';
import { useDurees } from './useReduceMotion';
import { FOND_VERRE, RAYON, VERT, VERT_CLAIR } from './tokens';

const TOTAL = 4;

/** Écran qui monte la carte, pour ne pas proposer d'aller là où l'on est déjà. */
export type ContexteCarte = 'destinations' | 'voyageurs' | 'demandes';

interface Props {
  contexte: ContexteCarte;
}

/**
 * « Votre feuille de route ». Ne rend RIEN tant que l'état serveur n'est pas connu :
 * les trois écrans qui la montent peuvent donc l'inclure sans condition.
 */
const CarteProgression: React.FC<Props> = ({ contexte }) => {
  const navigate = useNavigate();
  const { reduce, d } = useDurees();
  const { local, etapes, nbTerminees, indexCourant, doitAfficherCarte, masquer, replier } =
    useOnboarding();

  const [monte, setMonte] = React.useState(false);

  // Laisse la grille des pays se poser avant d'animer l'entrée, sinon la carte
  // provoque un saut de mise en page au tout premier rendu.
  React.useEffect(() => {
    if (!doitAfficherCarte) return;
    const t = setTimeout(() => setMonte(true), 150);
    return () => clearTimeout(t);
  }, [doitAfficherCarte]);

  if (!doitAfficherCarte) return null;

  const allerAEtape = () => {
    const etape = etapes[indexCourant];
    const visa = local.visaChoisi;
    if (!etape) return;

    if (etape.id === 'destination') {
      navigate('/retour', { state: { tabValue: 0 } });
      return;
    }
    if (etape.id === 'voyageur') {
      if (visa) {
        // MÊME contrat que VisasScreen (l.461-466) : returnUrl seul ne suffit pas.
        // Au retour ?fromCreation=true, le formulaire du pays lit sessionStorage.formState ;
        // sans lui il rouvre vide, affiche « Total : 0 € » et n'exige plus aucun champ
        // métier, puisque sa validation teste title.includes('tourisme' | 'affaire').
        try {
          sessionStorage.setItem('formState', JSON.stringify(visa.state));
        } catch (e) {
          console.warn('Impossible de mémoriser le contexte du visa.', e);
        }
      }
      const retour = visa ? `&returnUrl=${encodeURIComponent(visa.route)}` : '';
      navigate(`/formulaire_creation_voyageur?onboarding=1${retour}`);
      return;
    }
    if (etape.id === 'demande') {
      if (visa) navigate(visa.route, { state: visa.state });
      else navigate('/retour', { state: { tabValue: 0 } });
    }
  };

  const repliee = local.carteRepliee;

  // Seule l'étape « destination » peut viser l'écran courant : atteindre l'étape
  // « demande » suppose l'étape « destination » terminée, donc un visa mémorisé,
  // donc une route de formulaire — jamais un onglet.
  const etapeCourante = etapes[indexCourant];
  const indication =
    etapeCourante?.id === 'destination' && contexte === 'destinations'
      ? 'Choisissez un pays dans la liste ci-dessous'
      : undefined;

  return (
    <Grow in={monte} timeout={d(400)} style={{ transformOrigin: 'top center' }}>
      <Box
        component="section"
        aria-labelledby="feuille-de-route-titre"
        sx={{
          ...FOND_VERRE,
          borderRadius: RAYON,
          maxWidth: { xs: '100%', sm: 600 },
          mx: 'auto',
          mb: 2,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            id="feuille-de-route-titre"
            sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              letterSpacing: '1px',
              color: '#666',
              textTransform: 'uppercase',
              flex: 1,
            }}
          >
            Votre feuille de route
          </Typography>

          <Typography
            aria-live="polite"
            sx={{ fontSize: '1.05rem', fontWeight: 800, color: VERT, fontVariantNumeric: 'tabular-nums' }}
          >
            {nbTerminees} / {TOTAL}
          </Typography>

          <IconButton
            size="small"
            onClick={() => replier(!repliee)}
            aria-label={repliee ? 'Déplier la feuille de route' : 'Replier la feuille de route'}
          >
            {repliee ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
          </IconButton>
        </Box>

        <LinearProgress
          variant="determinate"
          value={(nbTerminees / TOTAL) * 100}
          sx={{
            mt: 1,
            height: 6,
            borderRadius: 3,
            backgroundColor: '#e0e0e0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
              background: `linear-gradient(90deg, ${VERT}, ${VERT_CLAIR})`,
              transition: reduce ? 'none' : 'transform 700ms cubic-bezier(0.4,0,0.2,1)',
            },
          }}
        />

        <Collapse in={!repliee} timeout={d(280)}>
          <Box component="ol" sx={{ m: 0, mt: 1, p: 0 }}>
            {etapes.map((etape, index) => (
              <Grow key={etape.id} in={monte} timeout={d(Math.min(280 + index * 110, 700))}>
                <Box>
                  <EtapeLigne
                    etape={etape}
                    numero={index + 1}
                    courante={index === indexCourant}
                    reduce={reduce}
                    d={d}
                    onAction={allerAEtape}
                    indication={index === indexCourant ? indication : undefined}
                  />
                </Box>
              </Grow>
            ))}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
            <Button
              size="small"
              onClick={masquer}
              sx={{ textTransform: 'none', color: '#999', fontSize: '0.78rem' }}
            >
              Ne plus afficher
            </Button>
          </Box>
        </Collapse>
      </Box>
    </Grow>
  );
};

export default CarteProgression;
